import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactElement,
} from 'react';
import { useLocation } from 'react-router-dom';

export interface HelpSection {
  title: string;
  content: string;
}

export interface HelpConfig {
  title: string;
  sections: HelpSection[];
}

export interface PageActionConfig {
  id: string;
  type: 'button' | 'chip' | 'icon-button';
  label: string;
  icon?: ReactElement;
  onClick?: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium';
  disabled?: boolean;
  chipValue?: string;
  showOnMobile?: boolean;
  hideOnDesktop?: boolean;
}

interface PageActionsContextValue {
  actions: PageActionConfig[];
  help: HelpConfig | null;
  setActions: (actions: PageActionConfig[]) => void;
  setHelp: (help: HelpConfig | null) => void;
  clear: () => void;
}

const PageActionsContext = createContext<PageActionsContextValue | undefined>(undefined);

export const PageActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [actions, setActionsState] = useState<PageActionConfig[]>([]);
  const [help, setHelpState] = useState<HelpConfig | null>(null);
  const location = useLocation();

  const setActions = useCallback((newActions: PageActionConfig[]) => {
    setActionsState(newActions);
  }, []);

  const setHelp = useCallback((newHelp: HelpConfig | null) => {
    setHelpState(newHelp);
  }, []);

  const clear = useCallback(() => {
    setActionsState([]);
    setHelpState(null);
  }, []);

  // Auto-clear on route change
  useEffect(() => {
    clear();
  }, [location.pathname, clear]);

  return (
    <PageActionsContext.Provider value={{ actions, help, setActions, setHelp, clear }}>
      {children}
    </PageActionsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const usePageActions = (): Pick<PageActionsContextValue, 'actions' | 'help'> => {
  const context = useContext(PageActionsContext);
  if (!context) {
    throw new Error('usePageActions must be used within a PageActionsProvider');
  }
  return { actions: context.actions, help: context.help };
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const useSetPageActions = (): Pick<
  PageActionsContextValue,
  'setActions' | 'setHelp' | 'clear'
> => {
  const context = useContext(PageActionsContext);
  if (!context) {
    throw new Error('useSetPageActions must be used within a PageActionsProvider');
  }
  return { setActions: context.setActions, setHelp: context.setHelp, clear: context.clear };
};
